import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Status } from 'utils/themeAppEmbed';
import { Button } from './Button';

interface Props {
  status: Status;
  editorAvailable: boolean;
  shopDomain?: string;
  connectionNeeded?: boolean;
  canManage?: boolean;
  activationContext?: 'home' | 'settings';
}

/** Guidance only: opening or completing these instructions records no verification. */
export const StorefrontSetupGuidance = ({
  status,
  editorAvailable,
  shopDomain,
  connectionNeeded = false,
  canManage = true,
  activationContext = 'settings',
}: Props) => {
  const [showCartCheck, setShowCartCheck] = useState(false);
  const enabled = status === 'enabled' && !connectionNeeded;
  const canActivate = status === 'disabled' && editorAvailable && !connectionNeeded;
  const title = connectionNeeded ? 'Store connection needed'
    : status === 'checking' ? 'Checking installation'
    : enabled ? 'Cart check needed'
    : canActivate ? 'Ready for merchant activation'
    : status === 'unavailable' ? 'Unable to verify'
    : 'Installation review needed';
  const storefrontUrl = typeof shopDomain === 'string' && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shopDomain)
    ? `https://${shopDomain.toLowerCase()}/` : null;

  return (
    <section aria-label="Installation next steps" className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
      <h3 className="font-semibold text-slate-950">{title}</h3>
      {connectionNeeded ? (
        <>
          <p className="mt-1">Ask your store administrator to review the existing ShipAid connection before continuing installation.</p>
          {canManage && <a className="mt-2 inline-block font-medium text-blue-700 underline" href="/settings/integration">Review store connection</a>}
        </>
      ) : status === 'checking' ? (
        <p className="mt-1">Wait for the installation check before choosing the next step.</p>
      ) : enabled ? (
        <p className="mt-1">Activation is confirmed on the published theme. Your cart still needs a separate check.</p>
      ) : canActivate ? (
        !canManage ? (
          <p className="mt-1">Ask your store administrator to enable ShipAid, save in Shopify, then check status. Theme activation requires appropriate store permissions.</p>
        ) : (
          <ol className="mt-2 list-decimal space-y-1.5 pl-5">
            <li>
              {activationContext === 'home' ? (
                <>
                  <Link className="font-medium text-blue-700 underline" to="/settings/protection">Open Delivery Guarantee settings</Link>. In the Theme app embed section, select Enable theme app embed.
                </>
              ) : (
                'Select Enable theme app embed above.'
              )}
            </li>
            <li>Review the ShipAid embed on the published theme, then click Save in Shopify.</li>
            <li>Return to ShipAid, select Check embed status, then follow the separate cart check.</li>
          </ol>
        )
      ) : status === 'unavailable' ? (
        <p className="mt-1">Retry the status check. If it remains unavailable, ask your store administrator or ShipAid support to review access. An unavailable check does not establish that the app is disconnected.</p>
      ) : (
        <p className="mt-1">Ask ShipAid support or your developer to confirm the installation route before changing the current setup. Headless stores and custom theme integrations need their own instructions.</p>
      )}
      <p className="mt-2 text-xs text-slate-600">Collaborator access is optional for self-service theme activation. If you choose assisted setup, we will identify the access needed for that work.</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {enabled && (
          <Button variant="outlined" onClick={() => setShowCartCheck(!showCartCheck)} props={{ 'aria-expanded': showCartCheck, 'aria-controls': 'storefront-cart-check' }}>
            {showCartCheck ? 'Hide cart check' : 'Show cart check'}
          </Button>
        )}
        <a href="https://help.shipaid.com" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 underline">Installation support</a>
      </div>
      {enabled && showCartCheck && (
        <div id="storefront-cart-check" className="mt-4 border-t border-slate-200 pt-4">
          <p>Use your own test cart on the published storefront. Keep your current ShipAid settings and use the cart page or drawer your shoppers use.</p>
          {storefrontUrl && <a href={storefrontUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block font-medium text-blue-700 underline">Open storefront for cart check</a>}
          <ol className="mt-3 list-decimal space-y-2 pl-5">
            <li>Add an available product. Check your selected ShipAid experience appears once and matches the saved price and wording.</li>
            <li>If shoppers can choose the Delivery Guarantee, test the configured opt-in and opt-out controls. Confirm the guarantee is added or removed once and merchandise stays in the cart.</li>
            <li>Change product quantity, close and reopen the cart, and refresh. Check totals and the Delivery Guarantee choice remain correct. Check discounts if your store uses them.</li>
            <li>Continue to checkout and confirm the expected merchandise, guarantee and totals. Do not place a paid order for this check.</li>
          </ol>
          <p className="mt-3">For merchant-paid coverage, confirm shoppers are not charged a guarantee fee; coverage and merchant billing need a separate check. Native checkout extensions also need their own checkout check.</p>
          <p className="mt-3">If a control fails, a guarantee appears twice, or checkout is blocked, stop the migration and contact installation support. Keep the existing working integration until the replacement has been verified.</p>
          <p className="mt-3 text-xs text-slate-600">This guide does not record a verified result. A saved embed or a visible widget alone does not prove the replacement loader works. Recheck after changing your published theme or cart app.</p>
        </div>
      )}
    </section>
  );
};
